/**
 * Typing Tamil with an English keyboard.
 *
 * <p>Written for the way dish names are actually typed - "idli", "vadai",
 * "sambar", "payasam" - rather than for a transliteration scheme with rules to
 * learn. Nothing leaves the browser: the whole table is here, so it works with
 * the office internet down and no dish name is sent anywhere to be converted.</p>
 *
 * <p>Tamil writes a consonant and a vowel as one letter: ka is க, ki is கி, and
 * a consonant with no vowel after it carries a pulli - க். So the text is read
 * consonant first, then whatever vowel follows it, and the two are written
 * together. A vowel with no consonant before it is written in its own
 * standalone form: அ, ஆ, இ.</p>
 */

/** Vowels in their own right, and the sign each becomes after a consonant. */
const VOWELS: ReadonlyArray<readonly [string, string, string]> = [
  // longest spellings first, so "aa" is read before "a"
  ['aa', 'ஆ', 'ா'],
  ['ee', 'ஈ', 'ீ'],
  ['ii', 'ஈ', 'ீ'],
  ['oo', 'ஊ', 'ூ'],
  ['uu', 'ஊ', 'ூ'],
  ['ai', 'ஐ', 'ை'],
  ['au', 'ஔ', 'ௌ'],
  ['ou', 'ஔ', 'ௌ'],
  ['ea', 'ஏ', 'ே'],
  ['oa', 'ஓ', 'ோ'],
  ['A', 'ஆ', 'ா'],
  ['I', 'ஈ', 'ீ'],
  ['U', 'ஊ', 'ூ'],
  ['E', 'ஏ', 'ே'],
  ['O', 'ஓ', 'ோ'],
  ['a', 'அ', 'ா'],
  ['i', 'இ', 'ி'],
  ['u', 'உ', 'ு'],
  ['e', 'எ', 'ெ'],
  ['o', 'ஒ', 'ொ'],
]

/**
 * Consonants, longest spelling first.
 *
 * <p>Tamil does not tell b from p, g from k, or d from t: one letter serves
 * both, and which sound it takes depends on where it sits in the word. So
 * "sambar" and "champar" arrive at the same ம்ப, and somebody typing what they
 * hear gets what they meant.</p>
 */
const CONSONANTS: ReadonlyArray<readonly [string, string]> = [
  ['ksh', 'க்ஷ'],
  ['zh', 'ழ'],
  ['ch', 'ச'],
  ['sh', 'ஷ'],
  ['th', 'த'],
  ['dh', 'த'],
  ['nj', 'ஞ'],
  ['gn', 'ஞ'],
  ['tr', 'ற்ற'],
  ['nd', 'ந்த'],
  ['nt', 'ந்த'],
  ['mb', 'ம்ப'],
  ['mp', 'ம்ப'],
  ['nk', 'ங்க'],
  ['ng', 'ங்க'],
  ['ks', 'க்ஸ'],
  ['jn', 'ஞ'],
  ['k', 'க'],
  ['g', 'க'],
  ['c', 'ச'],
  ['s', 'ச'],
  ['j', 'ஜ'],
  ['t', 'ட'],
  ['d', 'ட'],
  ['n', 'ன'],
  ['p', 'ப'],
  ['b', 'ப'],
  ['m', 'ம'],
  ['y', 'ய'],
  ['r', 'ர'],
  ['l', 'ல'],
  ['L', 'ள'],
  ['v', 'வ'],
  ['w', 'வ'],
  ['R', 'ற'],
  ['N', 'ண'],
  ['f', 'ஃப'],
  ['h', 'ஹ'],
  ['x', 'க்ஸ'],
  ['q', 'க'],
  ['z', 'ஸ'],
]

const PULLI = '்'

/** A word starting with one of these takes ஸ rather than ச. */
const GRANTHA_START = ['sr', 'sw', 'st', 'sp', 'sk', 'sn', 'sm']

const startsWith = (text: string, at: number, piece: string) =>
  text.startsWith(piece, at)

/**
 * Turns typed English into Tamil.
 *
 * <p>Anything it cannot read - a digit, a bracket, a space - is passed through
 * as it is, so "idli - 2" comes out as "இட்லி - 2" and a name half typed still
 * reads as itself.</p>
 */
export function toTamil(roman: string): string {
  if (!roman) return ''
  let out = ''
  let i = 0
  // Whether the letter just written was a consonant waiting for its vowel.
  let pending = false

  const wordStart = (at: number) => at === 0 || /[^A-Za-z]/.test(roman[at - 1] ?? ' ')

  while (i < roman.length) {
    const rest = roman.slice(i)

    // ஸ்ரீ, which is how it is always written and never spelled out
    if (/^sri/i.test(rest)) {
      out += (pending ? PULLI : '') + 'ஸ்ரீ'
      pending = false
      i += 3
      continue
    }

    // A consonant, longest spelling first.
    const consonant = CONSONANTS.find(([spelling]) =>
      spelling === spelling.toLowerCase()
        ? startsWith(roman.toLowerCase(), i, spelling)
        : startsWith(roman, i, spelling),
    )
    if (consonant) {
      const [spelling, letter] = consonant
      if (pending) out += PULLI
      // s before another consonant is the Grantha ஸ - "sri", "sweet", "stew" -
      // where s before a vowel is the Tamil ச.
      const grantha =
        spelling === 's'
        && GRANTHA_START.some((pair) => startsWith(roman.toLowerCase(), i, pair))
        && wordStart(i)
      out += grantha ? 'ஸ' : letter
      pending = true
      i += spelling.length
      continue
    }

    // A vowel: a sign on the letter before it, or a letter of its own.
    const vowel = VOWELS.find(([spelling]) =>
      spelling === spelling.toLowerCase()
        ? startsWith(roman.toLowerCase(), i, spelling)
        : startsWith(roman, i, spelling),
    )
    if (vowel) {
      const [spelling, standalone, sign] = vowel
      if (pending) {
        // "a" after a consonant is the sound the consonant already carries.
        out += spelling.toLowerCase() === 'a' ? '' : sign
        pending = false
      } else {
        out += standalone
      }
      i += spelling.length
      continue
    }

    // Anything else stands as it is.
    if (pending) {
      out += PULLI
      pending = false
    }
    out += roman[i]
    i += 1
  }

  if (pending) out += PULLI
  return out
}

/**
 * The words a menu is written in, spelled as the office spells them.
 *
 * <p>Read letter by letter, an English spelling comes out short: "masala" has
 * two long vowels that English does not write, so it arrives as மசல rather than
 * மசாலா, and every masala on the card would need correcting by hand. These are
 * the words that repeat across the catalogue - the ones worth knowing rather
 * than sounding out.</p>
 *
 * <p>Anything not here is still sounded out, so a new dish is never refused;
 * it is only spelled the way it is written.</p>
 */
const DISH_WORDS: Readonly<Record<string, string>> = {
  // the plate
  idli: 'இட்லி', idly: 'இட்லி', dosa: 'தோசை', dosai: 'தோசை', vada: 'வடை', vadai: 'வடை',
  pongal: 'பொங்கல்', upma: 'உப்புமா', poori: 'பூரி', puri: 'பூரி', chapati: 'சப்பாத்தி',
  chapathi: 'சப்பாத்தி', parotta: 'பரோட்டா', paratha: 'பராத்தா', naan: 'நான்',
  kulcha: 'குல்ச்சா', roti: 'ரொட்டி', uthappam: 'ஊத்தாப்பம்', oothappam: 'ஊத்தாப்பம்',
  bonda: 'போண்டா', bajji: 'பஜ்ஜி', murukku: 'முறுக்கு', mixture: 'மிக்சர்',

  // rice and the rest of the meal
  rice: 'ரைஸ்', biryani: 'பிரியாணி', briyani: 'பிரியாணி', pulao: 'புலாவ்', pulav: 'புலாவ்',
  meals: 'மீல்ஸ்', sambar: 'சாம்பார்', rasam: 'ரசம்', kootu: 'கூட்டு', poriyal: 'பொரியல்',
  kuzhambu: 'குழம்பு', curry: 'கறி', gravy: 'கிரேவி', curd: 'தயிர்', pickle: 'ஊறுகாய்',
  appalam: 'அப்பளம்', papad: 'அப்பளம்', chutney: 'சட்னி', podi: 'பொடி', masala: 'மசாலா',
  jeera: 'ஜீரா', noodles: 'நூடுல்ஸ்', soup: 'சூப்', salad: 'சாலட்', fried: 'ஃப்ரைடு',
  fry: 'ஃப்ரை', roast: 'ரோஸ்ட்', manchurian: 'மஞ்சூரியன்', combo: 'கொம்போ', meal: 'மீல்',

  // sweets
  sweet: 'ஸ்வீட்', sweets: 'ஸ்வீட்ஸ்', halwa: 'அல்வா', halva: 'அல்வா', burfi: 'பர்பி',
  barfi: 'பர்பி', katli: 'கத்லி', laddu: 'லட்டு', ladoo: 'லட்டு', jangiri: 'ஜாங்கிரி',
  jamun: 'ஜாமூன்', gulab: 'குலாப்', peda: 'பேடா', payasam: 'பாயசம்', kesari: 'கேசரி',
  mysore: 'மைசூர்', pak: 'பாக்', rasmalai: 'ரசமலாய்', rasgulla: 'ரசகுல்லா',
  kaju: 'காஜு', badam: 'பாதாம்', pista: 'பிஸ்தா', cashew: 'முந்திரி', boli: 'போளி',

  // what things are made of
  paneer: 'பன்னீர்', panneer: 'பன்னீர்', mushroom: 'மஷ்ரூம்', gobi: 'கோபி',
  cauliflower: 'காலிஃபிளவர்', onion: 'வெங்காயம்', garlic: 'பூண்டு', ginger: 'இஞ்சி',
  coconut: 'தேங்காய்', ghee: 'நெய்', butter: 'பட்டர்', milk: 'மில்க்', cream: 'கிரீம்',
  honey: 'தேன்', sugar: 'சர்க்கரை', salt: 'உப்பு', oil: 'எண்ணெய்', pepper: 'பெப்பர்',
  rava: 'ரவா', maida: 'மைதா', egg: 'முட்டை', chicken: 'சிக்கன்', mutton: 'மட்டன்',
  fish: 'மீன்', prawn: 'இறால்', veg: 'வெஜ்', vegetable: 'வெஜிடபிள்', alu: 'ஆலு',
  aloo: 'ஆலு', potato: 'உருளைக்கிழங்கு', batter: 'பேட்டர்', water: 'தண்ணீர்',

  // drinks and the sweet counter
  juice: 'ஜூஸ்', tea: 'டீ', coffee: 'காபி', lassi: 'லஸ்ஸி', soda: 'சோடா',
  milkshake: 'மில்க்ஷேக்', icecream: 'ஐஸ்கிரீம்', ice: 'ஐஸ்', kulfi: 'குல்பி',
  vanilla: 'வெனிலா', strawberry: 'ஸ்ட்ராபெரி', chocolate: 'சாக்லெட்', choco: 'சாக்கோ',
  mango: 'மாங்கோ', lemon: 'எலுமிச்சை', orange: 'ஆரஞ்சு', apple: 'ஆப்பிள்',
  pineapple: 'அன்னாசி', grape: 'திராட்சை', fruit: 'ஃப்ரூட்', dry: 'டிரை',

  // the words a price list is written with
  spl: 'ஸ்பெஷல்', special: 'ஸ்பெஷல்', mini: 'மினி', extra: 'எக்ஸ்ட்ரா', full: 'ஃபுல்',
  half: 'ஹாஃப்', small: 'ஸ்மால்', large: 'லார்ஜ்', plain: 'பிளைன்', assorted: 'அசார்ட்டட்',
  with: 'வித்', and: 'மற்றும்', gm: 'கிராம்', kg: 'கிலோ', ml: 'மி.லி', pcs: 'பீஸ்',
  piece: 'பீஸ்', tin: 'டின்', box: 'பாக்ஸ்', packet: 'பாக்கெட்', counter: 'கவுன்டர்',
  live: 'லைவ்', green: 'கிரீன்', red: 'ரெட்', white: 'ஒயிட்', hot: 'ஹாட்',
  cracker: 'கிராக்கர்', biscuit: 'பிஸ்கட்', cake: 'கேக்', bread: 'பிரெட்',
  rs: 'ரூ', carrot: 'கேரட்', peas: 'பீஸ்', offer: 'ஆஃபர்', yellow: 'எல்லோ',
  kofta: 'கோஃப்தா', biriyani: 'பிரியாணி', plate: 'பிளேட்', set: 'செட்', family: 'ஃபேமிலி',
  jumbo: 'ஜம்போ', cool: 'கூல்', drink: 'டிரிங்க்', sprite: 'ஸ்ப்ரைட்', limca: 'லிம்கா',
  pepsi: 'பெப்சி', coke: 'கோக்', monin: 'மோனின்',
}

/**
 * A whole name, written in Tamil: the words that are known, and the sounds of
 * the ones that are not.
 *
 * <p>What sits between the words - numbers, brackets, dashes - is left exactly
 * as it is, because "Gobi 65" and "Idli - 2" are read the same in either
 * script.</p>
 */
export function toTamilName(englishName: string): string {
  if (!englishName) return ''
  return englishName
    .split(/([^A-Za-z]+)/)
    .map((piece) => {
      if (!piece || /[^A-Za-z]/.test(piece)) return piece
      const known = DISH_WORDS[piece.toLowerCase()]
      return known ?? toTamil(piece.toLowerCase())
    })
    .join('')
}
